/**
 * Der Abschnitt „Zahlstatus" des Zahlungs-Reiters: DREI ZUSTAENDE statt „Zahlungsart festlegen"
 * (Spec 2026-09-17, Nachtrag 01.10.2026; Plan 2026-10-02, Aufgabe 3). Der Zustand folgt allein aus der
 * Herkunft des Plans (`order_extras.payment_plan_source`) und der Ratenzahl (`planArtVon`):
 *
 * - **Kein Zahlungsplan** (Herkunft NULL, keine Raten): Vorlage waehlen oder „Individuellen Plan
 *   anlegen" (Editor mit einer Rate 100 %, Bestelldatum, 0 Tage; erst „Speichern" schreibt).
 * - **Nach Plan** (Lieferantenstandard oder freigegebene Vorlage): Auswahl der verwendbaren
 *   Freigaben, darunter die Raten des Snapshots mit gesperrten Planfeldern; „Individuell anpassen".
 * - **Individuell**: „abgeleitet von <Titel>", der Editor, „Zurueck auf Vorlage" (mit Bestaetigung,
 *   gesperrt bei bezahlter oder fixierter Rate; fuer Sammelsendungs-Mitglieder nur mit der zentralen
 *   Vorlage).
 *
 * Zahlungsziel immer pflegbar (Ticket G): die Vorlagenauswahl ist in allen drei Zustaenden bedienbar
 * (ausser ohne Recht und schreibgeschuetzt). Am Mitglied einer Sammelsendung ist sie es nur, wenn sie
 * ausschliesslich festhaelt (G2, Nutzerentscheidung 2026-10-22 „An der Bestellung festhalten"): bei eigenem Plan
 * oder festgeschriebener Rate. Wuerde sie Raten ersetzen (kein Plan, eigener Plan OHNE Raten, Plan nach Vorlage mit
 * lauter offenen Raten), bleibt die Vorlage zentral in der Sammelsendung. Eine Auswahl geht ohne `raten`
 * an den Server (Standard `automatisch`): der erneuert die Raten nur, wenn der Plan einer Vorlage folgt und
 * keine Rate fixiert oder bezahlt ist, und haelt sonst nur das Zahlungsziel fest - im Zustand
 * „Individuell" und bei fixierten oder bezahlten Raten sagt ein Hinweis das vorab. Die ausdrueckliche
 * Absicht „Raten ersetzen" ist allein „Zurueck auf Vorlage" (`ersetzen`, bleibt bei fixierten oder
 * bezahlten Raten gesperrt).
 *
 * Eine Freigabe ist nur waehlbar, wenn ihre Vorlage verwendbar ist (`vorlageVerwendbar`, Spiegel des
 * Backends); eine unbrauchbare Vorlage steht deaktiviert mit dem Grund da. Eine Vorlage, die der
 * Lieferant nicht (mehr) freigegeben hat, bleibt sichtbar: ihr Titel stammt aus dem Snapshot, mit dem
 * Zusatz „(für diesen Lieferanten nicht freigegeben)" - nie ein leeres Auswahlfeld (E11).
 */
import { useState } from "react";
import { Abschnitt } from "../Abschnitt.tsx";
import { PlanEditor, Ratenplan } from "./Ratenplan.tsx";
import type { IstZahlungAktionen } from "./IstZahlung.tsx";
import { paymentsFromPiTerms, paymentStatusOf } from "../../../lib/payments.ts";
import {
  ersterEntwurf,
  entwurfAusAbgeleitetenRaten,
  planArtVon,
  vorlageMangel,
  type EntwurfRate,
  type PlanRateEingabe,
} from "../../../lib/zahlungsplanEditor.ts";
import type { Order, OrderPayment, Supplier } from "../../../lib/types.ts";
import type { FreigabeZeile } from "../../../api/lieferantenZahlungsziele.ts";
import type { ZahlungszielRaten } from "../../../api/bestellungZahlungsziel.ts";
import type { PaymentTemplateRow } from "../../../api/zahlungsvorlagen.ts";
import { Ikone } from "../../Ikone.tsx";
import { SAMMELSENDUNG_PFLEGEHINWEIS, sperrendeSammelsendung } from "../sammelsendungSperre.ts";

export interface VorlagenAuswahlProps {
  /** Die fuer die Anzeige normalisierte Bestellung - siehe `ZahlungTab.tsx`. */
  bestellung: Order;
  lieferanten: readonly Supplier[];
  /** Die fuer den Lieferanten dieser Bestellung freigegebenen Optionen. */
  freigaben: readonly FreigabeZeile[];
  /** Der Vorlagenkatalog mit `rows`; fehlt eine Vorlage, gilt sie als verwendbar (der Server entscheidet). */
  vorlagen?: readonly PaymentTemplateRow[];
  /**
   * Setzt die Option serverseitig (PUT /api/orders/:id/payment-terms). Die Auswahl ruft sie mit EINEM
   * Argument (Standard `automatisch`), „Zurueck auf Vorlage" mit `ersetzen`.
   */
  onZahlungszielOption: (templateId: string, raten?: ZahlungszielRaten) => void;
  /** „Individuell anpassen" (POST .../payment-plan/individualize). */
  onPlanIndividualisieren: () => void;
  /** „Speichern" im Editor (PUT .../payment-plan). */
  onPlanSpeichern: (raten: PlanRateEingabe[]) => void;
  schreibgeschuetzt?: boolean;
  /** Darf das Konto den Plan aendern (`manage_payments`)? Standard: ja. Siehe `PlanEditor.ohnePlanRecht`. */
  planRecht?: boolean;
  /** `patchPayment(i, key, value)` (`index.html:9441`) - durchgereicht an `Ratenplan`/`PlanEditor`. */
  onRate: (index: number, feld: keyof OrderPayment, wert: string) => void;
  /** Ist-Zahlung bedienen (nur mit `manage_payments`), durchgereicht an die Ratenkarten. */
  istAktionen?: IstZahlungAktionen;
}

const RUECKFRAGE =
  "Die eigenen Raten werden verworfen und durch die Raten der Vorlage ersetzt. Fortfahren?";

/** Unter der Vorlagenauswahl, wenn eine Auswahl die Raten NICHT erneuert, sondern nur das Zahlungsziel festhaelt. */
const FESTHALTEN_HINWEIS = "Die Raten bleiben unverändert – die Vorlage wird als Zahlungsziel festgehalten.";

/** Am Mitglied einer Sammelsendung (Zustand Individuell): wohin „Zurück auf Vorlage“ zielt. */
const MITGLIEDSHINWEIS = "Als Mitglied einer Sammelsendung geht „Zurück auf Vorlage“ nur auf deren zentrale Vorlage.";

export function VorlagenAuswahl({
  bestellung: o,
  lieferanten,
  freigaben,
  vorlagen,
  onZahlungszielOption,
  onPlanIndividualisieren,
  onPlanSpeichern,
  schreibgeschuetzt,
  planRecht = true,
  onRate,
  istAktionen,
}: VorlagenAuswahlProps) {
  const art = planArtVon(o);
  const raten = ((o.payments as OrderPayment[] | undefined) ?? []) as OrderPayment[];
  const gewaehlteOption = String(o.paymentTermsTemplateId ?? "");
  const snapshotLabel = String(o.paymentTerms ?? "");
  const sammelsendung = sperrendeSammelsendung(o);
  const zentraleVorlage = sammelsendung?.sharedValues.paymentTermsTemplateId ?? null;
  const freigegeben = freigaben.some((f) => f.templateId === gewaehlteOption);
  const eingefrorenAberNichtFreigegeben = gewaehlteOption !== "" && !freigegeben;

  // Kein Zahlungsplan: der Editor wird erst auf Wunsch geoeffnet; `startEntwurf` haelt seine Vorgabe.
  const [startEntwurf, setStartEntwurf] = useState<EntwurfRate[] | null>(null);
  // Der Entwurf gehoert nur zum Zustand „Kein Zahlungsplan": nach dem Speichern (Plan vorhanden) wird er
  // verworfen, sonst taucht er nach einem spaeteren Zuruecksetzen wieder auf. Ein anderer Datensatz
  // bekommt ueber `key={id}` in `ZahlungTab.tsx` ohnehin eine frische Instanz.
  if (art !== "keinPlan" && startEntwurf !== null) setStartEntwurf(null);

  const mangelVon = (templateId: string): string | null => {
    const vorlage = vorlagen?.find((v) => v.id === templateId);
    return vorlage ? vorlageMangel(vorlage.rows) : null;
  };
  const titelVon = (templateId: string): string => {
    if (templateId === gewaehlteOption && snapshotLabel !== "") return snapshotLabel;
    return freigaben.find((f) => f.templateId === templateId)?.label ?? templateId;
  };
  const optionsText = (f: FreigabeZeile): string => {
    const grund = mangelVon(f.templateId);
    const basis = titelVon(f.templateId) + (f.isDefault ? " (Standard)" : "");
    return grund ? `${basis} – nicht verwendbar (${grund})` : basis;
  };
  // Eine Rate, die nicht mehr offen ist, ist eine Ist-Zahlung bzw. fixiert: der Plan laesst sich nicht
  // mehr durch eine Vorlage ersetzen. Das Zahlungsziel selbst bleibt pflegbar (Ticket G): die Auswahl haelt
  // es dann nur fest.
  const festgeschrieben = raten.some((p) => paymentStatusOf(p) !== "open");
  // Spiegel von `ratenWerdenErsetzt` (Backend, Standard `automatisch`): ein eigener Plan und ein Plan mit
  // festgeschriebener Rate behalten ihre Raten, die Auswahl haelt nur das Zahlungsziel fest. Das gilt auch am
  // Mitglied einer Sammelsendung (G2): dort ist die Auswahl genau dann bedienbar, wenn sie nur festhaelt.
  const festhalten = raten.length > 0 && (art === "individuell" || festgeschrieben);
  const istMitglied = sammelsendung !== null;
  // Am Mitglied gesperrt, was Raten ersetzen wuerde: die Vorlage kommt dann zentral aus der Sammelsendung.
  const auswahlZentral = istMitglied && !festhalten;
  const festhaltenHinweis = festhalten ? <p className="plan-hinweis">{FESTHALTEN_HINWEIS}</p> : null;

  const vorlagenAuswahl = (
    wert: string,
    beiAenderung: (templateId: string) => void,
    gesperrt: boolean,
    titel?: string,
  ) => (
    <select
      id="zahlungsziel-auswahl"
      value={wert}
      disabled={gesperrt}
      title={titel}
      data-permission="manage_payments"
      onChange={(e) => {
        const templateId = e.currentTarget.value;
        if (templateId !== "") beiAenderung(templateId);
      }}
    >
      {wert === "" ? <option value="">— keine Option gewählt —</option> : null}
      {freigaben
        .map((f) => (
          <option key={f.templateId} value={f.templateId} disabled={mangelVon(f.templateId) !== null && f.templateId !== wert}>
            {optionsText(f)}
          </option>
        ))}
      {eingefrorenAberNichtFreigegeben ? (
        <option value={gewaehlteOption}>
          {`${snapshotLabel || gewaehlteOption} (für diesen Lieferanten nicht freigegeben)`}
        </option>
      ) : null}
    </select>
  );

  // ---------------------------------------------------------------- Kein Zahlungsplan
  if (art === "keinPlan") {
    const abgeleitet = paymentsFromPiTerms(o.paymentTerms, o);
    return (
      <Abschnitt schluessel="pay-plan" titel="Zahlstatus">
        <div className="pay-terms">
          <label htmlFor="zahlungsziel-auswahl">Zahlungsziel</label>
          <div className="pay-terms-row">
            {vorlagenAuswahl(
              gewaehlteOption,
              onZahlungszielOption,
              !!schreibgeschuetzt || auswahlZentral || startEntwurf !== null,
              auswahlZentral ? SAMMELSENDUNG_PFLEGEHINWEIS : undefined,
            )}
            {startEntwurf === null ? (
              <>
                <button
                  className="btn-ghost"
                  type="button"
                  disabled={schreibgeschuetzt || !planRecht}
                  data-permission="manage_payments"
                  onClick={() => setStartEntwurf(ersterEntwurf())}
                >
                  <Ikone name="circle-plus" />
                  Individuellen Plan anlegen
                </button>
                {/* E9: „In Raten umwandeln" fuellt NUR den Editor vor, gespeichert wird erst mit „Speichern". */}
                <button
                  className="btn-ghost"
                  type="button"
                  disabled={schreibgeschuetzt || !planRecht}
                  data-permission="manage_payments"
                  onClick={() => {
                    if (abgeleitet.length > 0) setStartEntwurf(entwurfAusAbgeleitetenRaten(abgeleitet));
                  }}
                  title="Aus dem alten Zahlungsziel-Text Raten mit Fälligkeit erzeugen"
                >
                  <Ikone name="list-ordered" />
                  In Raten umwandeln
                </button>
              </>
            ) : null}
          </div>
        </div>
        {startEntwurf === null ? (
          <div className="pay-sum" id="paySum">
            <span>Kein Zahlungsplan – Vorlage wählen oder einen individuellen Plan anlegen.</span>
          </div>
        ) : (
          <PlanEditor
            key="start"
            bestellung={o}
            gespeichert={raten}
            start={startEntwurf}
            schreibgeschuetzt={schreibgeschuetzt}
            ohnePlanRecht={!planRecht}
            onRate={onRate}
            istAktionen={istAktionen}
            onSpeichern={onPlanSpeichern}
            onVerwerfen={() => setStartEntwurf(null)}
          />
        )}
      </Abschnitt>
    );
  }

  // ---------------------------------------------------------------- Nach Plan
  if (art === "nachPlan") {
    return (
      <Abschnitt schluessel="pay-plan" titel="Zahlstatus">
        <div className="pay-terms">
          <label htmlFor="zahlungsziel-auswahl">Zahlungsziel</label>
          <div className="pay-terms-row">
            {/* Spec 2026-09-07: nur freigegebene Optionen (Freigabeliste des Lieferanten, NICHT der
                Katalog). Verwendbarkeit siehe Kopfkommentar. */}
            {vorlagenAuswahl(
              gewaehlteOption,
              onZahlungszielOption,
              !!schreibgeschuetzt || auswahlZentral,
              auswahlZentral ? SAMMELSENDUNG_PFLEGEHINWEIS : undefined,
            )}
            <button
              className="btn-ghost"
              type="button"
              disabled={schreibgeschuetzt}
              data-permission="manage_payments"
              title="Raten unverändert übernehmen und frei bearbeitbar machen"
              onClick={onPlanIndividualisieren}
            >
              Individuell anpassen
            </button>
          </div>
          {festhaltenHinweis}
          <p className="plan-hinweis">
            Plan nach Vorlage: Anteil, Betrag, Anker, Tage und Fälligkeit sind gesperrt. Zahlstatus,
            Zahlungsdatum und Dollarkurs bleiben bearbeitbar.
          </p>
        </div>
        <Ratenplan bestellung={o} lieferanten={lieferanten} onRate={onRate} planGesperrt istAktionen={istAktionen} />
      </Abschnitt>
    );
  }

  // ---------------------------------------------------------------- Individuell
  const abgeleitetVon = gewaehlteOption !== "" ? titelVon(gewaehlteOption) : null;
  // Mitglied einer Sammelsendung: „Zurueck auf Vorlage" zielt nur auf die zentrale Vorlage der Sendung, nicht auf die am
  // Mitglied festgehaltene. Sonst ist das Ziel die festgehaltene Vorlage - die Auswahl darueber speichert sofort,
  // „Zurueck auf Vorlage" ersetzt danach die Raten.
  const zielVorlage = istMitglied ? zentraleVorlage ?? "" : gewaehlteOption;
  const zielMangel = zielVorlage !== "" ? mangelVon(zielVorlage) : null;
  // Der Grund gilt dem Knopf, nicht der Auswahl: die haelt bei festgeschriebener Rate nur fest und bleibt bedienbar.
  const rueckGrund = festgeschrieben
    ? "„Zurück auf Vorlage“ ist nicht möglich, solange eine Rate bezahlt oder fixiert ist."
    : zielVorlage === ""
      ? istMitglied
        ? "Die Sammelsendung hat keine zentrale Vorlage."
        : "Bitte zuerst eine Vorlage wählen."
      : !freigaben.some((f) => f.templateId === zielVorlage)
        ? `Die ${istMitglied ? "zentrale " : ""}Vorlage ist für diesen Lieferanten nicht freigegeben.`
        : zielMangel !== null
        ? `Die ${istMitglied ? "zentrale " : ""}Vorlage ist nicht verwendbar (${zielMangel}).`
        : null;
  return (
    <Abschnitt schluessel="pay-plan" titel="Zahlstatus">
      <div className="pay-terms">
        <div className="plan-herkunft">
          <strong>Individueller Zahlungsplan</strong>
          {abgeleitetVon !== null ? <span>abgeleitet von {abgeleitetVon}</span> : null}
        </div>
        <label htmlFor="zahlungsziel-auswahl">Vorlage</label>
        <div className="pay-terms-row">
          {vorlagenAuswahl(
            gewaehlteOption,
            onZahlungszielOption,
            !!schreibgeschuetzt || auswahlZentral,
            auswahlZentral ? SAMMELSENDUNG_PFLEGEHINWEIS : undefined,
          )}
          <button
            className="btn-ghost"
            type="button"
            disabled={schreibgeschuetzt || rueckGrund !== null}
            data-permission="manage_payments"
            title={
              rueckGrund ??
              (istMitglied
                ? "Eigene Raten verwerfen und die zentrale Vorlage der Sammelsendung übernehmen"
                : "Eigene Raten verwerfen und die Vorlage übernehmen")
            }
            onClick={() => {
              if (rueckGrund !== null || zielVorlage === "") return;
              if (!confirm(RUECKFRAGE)) return;
              onZahlungszielOption(zielVorlage, "ersetzen");
            }}
          >
            Zurück auf Vorlage
          </button>
        </div>
        {festhaltenHinweis}
        {rueckGrund !== null && (festgeschrieben || istMitglied || !freigaben.some((f) => f.templateId === zielVorlage)) ? <p className="plan-hinweis">{rueckGrund}</p> : null}
        {istMitglied ? <p className="plan-hinweis">{MITGLIEDSHINWEIS}</p> : null}
      </div>
      <PlanEditor
        key="plan"
        bestellung={o}
        gespeichert={raten}
        schreibgeschuetzt={schreibgeschuetzt}
        ohnePlanRecht={!planRecht}
        onRate={onRate}
        istAktionen={istAktionen}
        onSpeichern={onPlanSpeichern}
      />
    </Abschnitt>
  );
}
